import { apiClient } from './client';

// Lesson video upload. The file never passes through the API: the backend signs a PUT straight
// to the private video bucket, the browser sends the bytes there, then confirm starts the
// transcoder (a Cloud Run Job that writes HLS) and status is polled until READY or FAILED.
// READY means Lesson.videoMediaId points at the new video, which every app plays through the
// stream-token and HLS proxy.

/** Upload cap the backend enforces (LESSON_VIDEO_MAX_BYTES); the signed URL refuses larger files. */
export const MAX_VIDEO_BYTES = 2_000_000_000;

/** Types the backend accepts, keyed by file extension for files whose File.type is empty. */
const TYPE_BY_EXTENSION = {
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  '3gp': 'video/3gpp',
};
const ACCEPTED_TYPES = new Set(Object.values(TYPE_BY_EXTENSION));

/** The `accept` attribute of the file input. */
export const VIDEO_ACCEPT = [...ACCEPTED_TYPES, ...Object.keys(TYPE_BY_EXTENSION).map((ext) => `.${ext}`)].join(',');

/** The content type to declare for a file, or null when the backend would refuse it. */
export function videoContentType(file) {
  const declared = (file?.type || '').toLowerCase();
  if (ACCEPTED_TYPES.has(declared)) return declared;
  if (declared && !declared.startsWith('video/')) return null;
  const extension = (file?.name || '').split('.').pop().toLowerCase();
  return TYPE_BY_EXTENSION[extension] || null;
}

/**
 * Ask for a signed upload URL.
 * @returns {Promise<{ uploadUrl, method, headers, objectKey, expiresAt, maxBytes }>}
 */
export async function requestLessonVideoUpload(lessonId, file) {
  const { data } = await apiClient.post(`/lessons/${lessonId}/video/upload-url`, {
    fileName: file.name,
    contentType: videoContentType(file),
    size: file.size,
  });
  return data;
}

/**
 * PUT the file to the signed URL with real progress. No timeout (a large lesson takes minutes),
 * no cookies or API headers (the bucket is another origin), abortable through `signal`.
 * @param {{ uploadUrl: string, headers: Record<string,string> }} upload
 * @param {File} file
 * @param {{ onProgress?: (loaded: number, total: number) => void, signal?: AbortSignal }} options
 */
export function putLessonVideoFile(upload, file, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload.uploadUrl);
    Object.entries(upload.headers || {}).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      if (onProgress) onProgress(event.loaded, event.lengthComputable ? event.total : file.size);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(Object.assign(new Error(`Upload failed: ${xhr.status}`), { status: xhr.status, body: xhr.responseText }));
    };
    xhr.onerror = () => reject(Object.assign(new Error('Upload failed: network error'), { status: 0 }));
    xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { cancelled: true }));
    if (signal) {
      if (signal.aborted) {
        reject(Object.assign(new Error('Upload cancelled'), { cancelled: true }));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

/**
 * Tell the backend the upload is in the bucket; it starts the transcoder. Confirming the same
 * objectKey again after a failure re-runs the transcoder on that file.
 * @returns {Promise<LessonVideoStatus>} the same shape as getLessonVideoStatus
 */
export async function confirmLessonVideo(lessonId, objectKey, fileName) {
  const { data } = await apiClient.post(`/lessons/${lessonId}/video/confirm`, {
    objectKey,
    ...(fileName ? { fileName } : {}),
  });
  return data;
}

/**
 * @returns {Promise<{
 *   lessonId: string, videoMediaId: string|null, videoDuration: number|null,
 *   upload: null | { mediaId, state: 'PROCESSING'|'READY'|'FAILED', progress: number|null,
 *     errorCode: string|null, fileName, size, objectKey, duration, live: boolean, createdAt, updatedAt }
 * }>}
 */
export async function getLessonVideoStatus(lessonId) {
  const { data } = await apiClient.get(`/lessons/${lessonId}/video/status`);
  return data;
}
